<script setup lang="ts">
import { ScanLine, X } from 'lucide-vue-next';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { t } from '../i18n';

type DetectedBarcode = { rawValue: string };
type BarcodeDetectorInstance = {
    detect(source: HTMLVideoElement): Promise<DetectedBarcode[]>;
};
type BarcodeDetectorConstructor = new (options: {
    formats: string[];
}) => BarcodeDetectorInstance;

const props = defineProps<{
    closeScanner: () => void;
    handleDetected: (payload: string) => void;
}>();
const video = ref<HTMLVideoElement | null>(null);
const cameraError = ref('');
let stream: MediaStream | null = null;
let scanFrame = 0;
let detector: BarcodeDetectorInstance | null = null;

onMounted(async () => {
    try {
        stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: 'environment' } },
            audio: false,
        });

        if (!video.value) {
            return;
        }

        video.value.srcObject = stream;
        await video.value.play();

        const Detector = (
            window as typeof window & {
                BarcodeDetector?: BarcodeDetectorConstructor;
            }
        ).BarcodeDetector;

        if (Detector) {
            detector = new Detector({ formats: ['qr_code'] });
            scanFrame = window.requestAnimationFrame(scanVideo);
        }
    } catch {
        cameraError.value = t(
            'Не вдалося відкрити камеру. Дозвольте доступ у налаштуваннях браузера.',
        );
    }
});

onBeforeUnmount(() => {
    window.cancelAnimationFrame(scanFrame);
    stream?.getTracks().forEach((track) => track.stop());
});

async function scanVideo(): Promise<void> {
    if (!video.value || !detector) {
        return;
    }

    try {
        const [result] = await detector.detect(video.value);

        if (result?.rawValue) {
            props.handleDetected(result.rawValue);

            return;
        }
    } catch {
        // Some browsers reject frames while the camera is warming up.
    }

    scanFrame = window.requestAnimationFrame(scanVideo);
}
</script>

<template>
    <div class="preview-scanner" role="dialog" aria-modal="true">
        <header>
            <div>
                <h2>{{ t('Наведіть камеру на код') }}</h2>
            </div>
            <button
                type="button"
                :aria-label="t('Закрити')"
                @click="props.closeScanner"
            >
                <X :size="20" />
            </button>
        </header>
        <div class="camera-frame">
            <video ref="video" playsinline muted />
            <div class="scan-guides"><i /><i /><i /><i /></div>
            <div class="scan-line" />
            <p v-if="cameraError">{{ cameraError }}</p>
        </div>
        <button
            class="demo-scan"
            type="button"
            @click="props.handleDetected('pc_preview')"
        >
            <ScanLine :size="18" /> {{ t('Демо QR від ПК') }}
        </button>
    </div>
</template>

<style scoped>
.preview-scanner {
    position: fixed;
    z-index: 70;
    inset: 0;
    display: flex;
    flex-direction: column;
    padding: calc(20px + var(--app-safe-area-top)) 16px
        calc(20px + env(safe-area-inset-bottom));
    background: #070d16;
    color: white;
}
header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 18px;
}
header h2 {
    margin: 0;
    font-size: 20px;
}
header button {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border: 0;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    color: white;
}
.camera-frame {
    position: relative;
    flex: 1;
    min-height: 300px;
    overflow: hidden;
    border-radius: 28px;
    background: #111b28;
}
.camera-frame video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.camera-frame > p {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    margin: 0;
    padding: 30px;
    color: #d7e0ec;
    text-align: center;
    font-size: 13px;
    line-height: 1.5;
}
.scan-guides {
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(66vw, 270px);
    aspect-ratio: 1;
    transform: translate(-50%, -50%);
}
.scan-guides i {
    position: absolute;
    width: 46px;
    height: 46px;
    border-color: #2997ff;
    border-style: solid;
}
.scan-guides i:nth-child(1) {
    top: 0;
    left: 0;
    border-width: 4px 0 0 4px;
    border-radius: 16px 0 0;
}
.scan-guides i:nth-child(2) {
    top: 0;
    right: 0;
    border-width: 4px 4px 0 0;
    border-radius: 0 16px 0 0;
}
.scan-guides i:nth-child(3) {
    right: 0;
    bottom: 0;
    border-width: 0 4px 4px 0;
    border-radius: 0 0 16px;
}
.scan-guides i:nth-child(4) {
    bottom: 0;
    left: 0;
    border-width: 0 0 4px 4px;
    border-radius: 0 0 0 16px;
}
.scan-line {
    position: absolute;
    top: 50%;
    left: 20%;
    right: 20%;
    height: 2px;
    background: #2997ff;
    box-shadow: 0 0 16px #2997ff;
    animation: scan 2s infinite ease-in-out;
}
.demo-scan {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 50px;
    margin-top: 14px;
    border: 0;
    border-radius: 14px;
    background: #2997ff;
    color: white;
    font: 600 14px/1 var(--app-font);
}
@keyframes scan {
    0%,
    100% {
        transform: translateY(-110px);
    }
    50% {
        transform: translateY(110px);
    }
}
</style>
